import { getApiEndpoint } from "../utils/constants/api";
import { SHOP_SOURCES } from "../utils/constants/shopSources";
import { wfetch } from "../utils/fetch";
import { parseRef } from "../utils/refs";
import { isUuid } from "../utils/uuid";

// The visual merchandiser stores the curated product order (+ banners) as `merchandisingJson` on
// the category node. For external sources it lives on the synced node in the ecommerce-categories
// JCR workspace, addressable by source + externalId. Returns the ordered items array, or null.
export const getMerchandising = async (opts) => {
    const { source, categoryId, category } = opts;
    let raw;
    if (source === SHOP_SOURCES.JCR) {
        raw = category && category.merchandisingJson;
    } else {
        try {
            const res = await wfetch(
                getApiEndpoint("CATEGORIES", { queryParams: { "source[eq]": source, "externalId[eq]": categoryId } }),
            );
            const results = res?.data?.results || [];
            const node = results.find((n) => n.externalId === categoryId) || results[0];
            raw = node && node.merchandisingJson;
        } catch (e) {
            // Network / HTTP failure must not blank the whole grid — fall back to default order.
            return null;
        }
    }
    // The delivery API HTML-escapes string properties, so merchandisingJson comes back as
    // {&quot;items&quot;:…}; parseRef decodes that before parsing.
    const parsed = parseRef(raw);
    return Array.isArray(parsed?.items) ? parsed.items : null;
};

export const getCategories = async (opts) => {
    const { source } = opts;

    // JCR source
    if (source === SHOP_SOURCES.JCR) {
        const { lang } = opts;

        const res = await wfetch(getApiEndpoint("CATEGORIES", { queryParams: { lang } }));

        return res?.data?.results;
    }

    // External sources
    const res = await wfetch(getApiEndpoint("EXTERNAL_CATEGORIES", { pathParams: { source } }));

    return res?.data?.results && composeCategoriesTree(res.data.results, source);
};

export const getCategory = async (opts) => {
    const { source, categoryId } = opts;

    // JCR source
    if (source === SHOP_SOURCES.JCR) {
        const { lang } = opts;

        // The id is either a JCR path or a uuid (links built from resolver DTOs carry @id).
        // Look up by whichever shape arrived — same pattern as fetchBrand's @jcr:uuid filter.
        if (isUuid(categoryId)) {
            const res = await wfetch(getApiEndpoint("CATEGORIES", { queryParams: { lang, "@jcr:uuid": categoryId } }));

            return res?.data?.results && res?.data?.results[0];
        }

        // JCR delivery @path must be ABSOLUTE. A slash-less value (e.g. "flone-demo" from a stored
        // @name / relative ref) makes the endpoint return 400 invalidQuery — normalise the leading slash.
        const path = categoryId?.startsWith("/") ? categoryId : `/${categoryId}`;
        const res = await wfetch(getApiEndpoint("CATEGORIES", { queryParams: { lang, "@path": path } }));

        return res?.data?.results && res?.data?.results[0];
    }

    // External sources
    const res = await wfetch(getApiEndpoint("EXTERNAL_CATEGORIES", { pathParams: { source, categoryId: encodeURIComponent(categoryId) } }));

    return res?.data;
};

export const composeCategoriesTree = (categories, source) => {
    if (source === SHOP_SOURCES.JCR) return categories;

    const map = categories.reduce((acc, item) => ({ ...acc, [item["@id"]]: { ...item, "@nodes": [] } }), {});

    return Object.values(
        categories.reduce((acc, { "@id": id, parentId }) => {
            if (parentId && acc[parentId]) {
                acc[parentId][id] = acc[id];
                acc[parentId]["@nodes"].push(id);
            }
            return acc;
        }, map),
    ).filter(({ parentId }) => !parentId);
};

export const getCategoryTreeIds = (category) => {
    const ids = new Set([]);

    if (category["@id"]) ids.add(category["@id"]);

    if (category["@nodes"] && category["@nodes"].length)
        category["@nodes"].forEach((nodeName) => category[nodeName] && ids.add(...getCategoryTreeIds(category[nodeName])));

    return ids;
};